import { useEffect, useRef } from 'react';
import { getInternals, isEditingInContext } from './workbookInternals';
import { matchShortcut, isGridKeyTarget, isNativeEditableElement } from './shortcutKeymap';
import { gateLaneAKey } from './editGate';
import { dispatchLaneA } from './keyDispatch';
import { handleEscapeEvent } from './escapeHandler';
import { saveShortcut, printGuard, fillDownDblClick } from './sheetActions';
import { FILL_HANDLE_CLASS } from './selectors';
import type { UseSheetKeyboardShortcutsArgs, UseSheetKeyboardShortcutsReturn } from './useSheetKeyboardShortcuts.types';

export type { UseSheetKeyboardShortcutsArgs, UseSheetKeyboardShortcutsReturn } from './useSheetKeyboardShortcuts.types';

const isNativeEditableTarget = (target: EventTarget | null): boolean => target instanceof Element && isNativeEditableElement(target);
/** Modifier combos and F1-F12 (reload, devtools, …) stay with the browser. */
const isBrowserShortcut = (e: React.KeyboardEvent): boolean => e.ctrlKey || e.metaKey || e.altKey || /^F([1-9]|1[0-2])$/.test(e.key);
const isFillHandleTarget = (target: EventTarget | null): boolean =>
    target instanceof Element && target.classList.contains(FILL_HANDLE_CLASS);

/**
 * Lane A (`onKeyDownCapture` on the grid wrapper) + Lane B (a document
 * capture listener while `open`) — see `shortcutKeymap.ts` for the
 * partition. Wiring only: the gate/dispatch/escape logic itself lives in
 * `editGate.ts` / `keyDispatch.ts` / `escapeHandler.ts`.
 */
export const useSheetKeyboardShortcuts = ({
    workbook,
    gates,
    dialogs,
    actions,
}: UseSheetKeyboardShortcutsArgs): UseSheetKeyboardShortcutsReturn => {
    const { open, workbookRef, historyOpen, readOnly = false } = workbook;
    const mutationsBlocked = historyOpen || readOnly;
    const { isNameBoxTarget = () => false } = dialogs;

    // Lane B's listener is registered once per `open` toggle (not re-bound on
    // every render) — it always reads the LATEST render's values through
    // this ref instead (mirrors useSheetEditorDrawerLogic.ts's runAutosaveRef).
    const latestRef = useRef({ workbook, gates, dialogs, actions, isNameBoxTarget });
    latestRef.current = { workbook, gates, dialogs, actions, isNameBoxTarget };

    const handleKeyDownCapture = (e: React.KeyboardEvent): void => {
        if (!isGridKeyTarget(e.target)) return;
        // A sheet dialog is modal: keys that still reach the grid (focus can
        // linger on its cell input) must neither move the selection nor edit.
        if (dialogs.dialogOpen && !isBrowserShortcut(e)) {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        const internals = getInternals(workbookRef.current);
        const editing = isEditingInContext(internals?.getContext());
        const actionId = matchShortcut(e, 'A', { editing });

        if (gateLaneAKey(e, { historyOpen: mutationsBlocked, actionId }) === 'block') {
            e.preventDefault();
            e.stopPropagation();
            return;
        }
        if (!actionId) return;

        const result = dispatchLaneA(actionId, {
            internals,
            wb: workbookRef.current,
            target: e.target,
            key: e.key,
            code: e.code,
            shiftKey: e.shiftKey,
            onOpenGoTo: dialogs.onOpenGoTo,
            onOpenFormatCells: dialogs.onOpenFormatCells,
            onArmValuesPaste: dialogs.onArmValuesPaste,
            onOpenPasteSpecial: dialogs.onOpenPasteSpecial,
            onOpenDeleteInsert: dialogs.onOpenDeleteInsert,
        });
        if (result === 'handled') {
            e.preventDefault();
            e.stopPropagation();
        }
    };

    const handleDoubleClickCapture = (e: React.MouseEvent): void => {
        // Fill-handle double-click; a plain cell double-click is left alone (library's dblclick-to-edit still runs).
        if (!isFillHandleTarget(e.target) || mutationsBlocked) return;
        e.stopPropagation();
        const wb = workbookRef.current;
        const sel = wb?.getSelection?.()?.[0];
        if (wb && sel) fillDownDblClick(wb, { row: [sel.row[0], sel.row[1]], column: [sel.column[0], sel.column[1]] });
    };

    /** Lane B's Esc handling (`escapeHandler.ts`'s rule 0) — always reads the
     *  LATEST render's state via `latestRef` since this is called from a
     *  listener registered once per `open` toggle, not re-bound on every
     *  render. */
    const handleEscape = (e: KeyboardEvent): void => {
        const latest = latestRef.current;
        handleEscapeEvent(e, {
            internals: getInternals(latest.workbook.workbookRef.current),
            root: latest.workbook.wrapperRef.current,
            workbookMounted: latest.workbook.workbookMounted,
            dialogOpen: latest.dialogs.dialogOpen,
            isNameBoxTarget: latest.isNameBoxTarget,
            keytipsOpen: Boolean(latest.dialogs.keytipsOpen),
            onCloseDialog: latest.dialogs.onCloseDialog,
            onClose: latest.actions.onClose,
            onCloseKeytipsLevel: latest.dialogs.onCloseKeytipsLevel,
            focusCellInput: latest.actions.focusCellInput,
        });
    };

    /** Lane B's document-capture keydown listener — dispatches Save/print-guard/
     *  Esc/the focus-lost fallback, all via `latestRef` for the same reason
     *  `handleEscape` above does. */
    const handler = (e: KeyboardEvent): void => {
        const outsideGridEditable = !isGridKeyTarget(e.target) && isNativeEditableTarget(e.target);
        const actionId = matchShortcut(e, 'B', { editing: false });
        if (outsideGridEditable && !actionId) return; // Lane B ignores these except S/P/Esc

        const latest = latestRef.current;
        if (actionId === 'save') {
            saveShortcut({
                e,
                canSave: latest.workbook.canSave,
                historyOpen: latest.workbook.historyOpen,
                internals: getInternals(latest.workbook.workbookRef.current),
                handleSave: latest.actions.handleSave,
                hasPendingChanges: latest.gates.hasPendingChanges,
            });
        } else if (actionId === 'printGuard') {
            printGuard(e, latest.gates.handleDownloadXlsx);
        } else if (actionId === 'resolveEscape') {
            handleEscape(e);
        } else {
            latest.actions.refocusAndRedispatch(e); // no-ops internally unless target === document.body
        }
    };

    useEffect(() => {
        if (!open) return undefined;
        document.addEventListener('keydown', handler, true);
        return () => document.removeEventListener('keydown', handler, true);
        // handler is recreated every render but only ever reads `latestRef`
        // (a stable ref) — omitted here so the listener registers once per
        // `open` toggle instead of every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    return { handleKeyDownCapture, handleDoubleClickCapture };
};
